import AccommodationIndividualSelectRoomOptionTile from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTile'
import AccommodationIndividualSelectRoomOptionTileDetails from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileDetails'
import AccommodationIndividualSelectRoomOptionTilePricing from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTilePricing'
import ProductTypeLabel from 'LuxKit/Label/ProductTypeLabel'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import BedbankCancellationPolicy from '../BedbankCancellationPolicy'
import BedbankPromotionDetails from '../BedbankPromotionDetails'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import WalledContent from 'components/Common/WalledContent'
import BedbankMultiIndividualOptionTilePricing from './BedbankMultiIndividualOptionTilePricing'
import moment from 'moment'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import AccommodationIndividualSelectRoomOptionTileCTA from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileCTA'
import TextButton from 'LuxKit/Button/TextButton'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBedbankOptions, generateLoyaltyPointsCalculatorBundledFlightOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import GeoContext from 'contexts/geoContext'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import AccommodationOfferInclusionsExpandable from 'components/OfferPage/Accommodation/AccommodationOfferInclusionsExpandable'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import useSaleUnitFormatting from 'hooks/useSaleUnitFormatting'
import AccommodationIndividualSelectRoomOptionTileMessage from 'components/AccommodationKit/AccommodationIndividualSelectRoom/AccommodationIndividualSelectRoomOptionTileMessage'
import { rem } from 'polished'
import styled from 'styled-components'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { FLIGHTS_MIN_LEAD_TIME_DAYS } from 'constants/flight'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'

const LoyaltyPointsWrapper = styled.div`
  padding-block: ${rem(2)}; // To compensate for the loyalty coin which protrudes a couple of pixels outside of its container

  &:empty {
    display: none;
  }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  packageNumber: number
  rate: App.BedbankRate
  packageView: App.BedbankOfferPackageView
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  rooms: Array<OfferPageRoom>
  flightOrigin?: App.Airport
  rateListId?: string
  roomListId?: string
  roomPosition?: number
  currentRoom: OfferPageRoom
  onOptionSelect: (packageOption: App.BedbankPackage, rate: App.BedbankRate) => void
  flightsEnabled: boolean
  isFlightsSelected: boolean
  hasAvailability: boolean
  variant: 'stacked' | 'carousel'
}

function BedbankIndividualRoomSelectRoomOption(props: Props) {
  const {
    rate,
    packageNumber,
    packageView,
    checkInDate,
    checkOutDate,
    offer,
    rooms,
    flightOrigin,
    rateListId,
    roomListId,
    roomPosition,
    currentRoom,
    onOptionSelect,
    flightsEnabled,
    isFlightsSelected,
    hasAvailability,
    variant,
  } = props

  const intl = useIntl()
  const { getSaleUnitPluralNoun } = useSaleUnitFormatting()
  const { currentRegionCode } = useContext(GeoContext)
  const promotion = packageView.promotions[rate?.promotionId ?? '']

  const departureTooEarly = useMemo(() => {
    return !!checkInDate && moment(checkInDate).diff(moment(), 'days') < FLIGHTS_MIN_LEAD_TIME_DAYS
  }, [checkInDate])

  const totalFlightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })
  const roomPax = sumUpOccupancies([currentRoom])
  const totalPax = sumUpOccupancies(rooms)
  const flightPriceProportion = roomPax / totalPax

  const flightPrice = totalFlightPrice ? totalFlightPrice * flightPriceProportion : undefined

  const hasFlights = !!flightPrice

  const showHotelAndFlightsToggle = flightsEnabled
  const hideHotelAndFlightsPriceBlock = showHotelAndFlightsToggle && !isFlightsSelected

  const canShowFlightBundlePrice = flightsEnabled && hasFlights && !departureTooEarly

  const isSelected = packageView.package.roomId === currentRoom.roomId && rate.id === currentRoom.rateId

  const isDesktopLayout = useScreenSizeUp('tablet')

  const onSelect = useCallback(() => {
    onOptionSelect(packageView.package, rate)
  }, [onOptionSelect, packageView.package, rate])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    if (!rate) { return [] }
    return [
      generateLoyaltyPointsCalculatorBedbankOptions(rate, {
        price: rate.totals.inclusive,
        memberPrice: rate.totals.memberInclusive,
        propertyFees: rate.totals.propertyFees ?? 0,
        propertyId: offer.property.id,
        offerId: offer.id,
        propertyCountryCode: offer.property.address.countryCode,
        currentRegionCode,
      }),
      generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
        flightPrice,
        skip: !canShowFlightBundlePrice || hideHotelAndFlightsPriceBlock,
      }),
    ]
  }, [canShowFlightBundlePrice, currentRegionCode, flightPrice, hideHotelAndFlightsPriceBlock, offer, rate])

  const selectedLabel = intl.formatMessage({
    id: 'tof1B_IFtTgEDJ5pZS5M',
    defaultMessage: 'Selected',
    description: 'Button label indicating this room option is currently selected on bedbank individual room select',
  })
  const selectLabel = intl.formatMessage({
    id: 'U17kvExCm28eLntAA8ff',
    defaultMessage: 'Select',
    description: 'Button label to select a room option on bedbank individual room select',
  })

  return <AccommodationIndividualSelectRoomOptionTile isSelected={isSelected}>
    <AccommodationIndividualSelectRoomOptionTileDetails
      variant={variant}
      packageNumber={packageNumber}
      promotion={promotion}
      tagLineLabel={<>
        {rate.corporate && <ProductTypeLabel productType="corporate" />}
        {promotion && <ProductTypeLabel productType="limited_time_special" />}
      </>}
      cancellationPolicy={<>
        {promotion && <BedbankPromotionDetails
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
          promotion={promotion}
          rate={rate}
        />}
        {!promotion && <BedbankCancellationPolicy
          rate={rate}
          showNonCancellation
        />}
      </>}
    >
      <AccommodationOfferInclusionsExpandable
        content={promotion?.rateInclusionsLong}
        inclusions={promotion?.inclusions ?? rate.inclusions}
        maxHeight={80}
        collapsedHeight={70}
        additionalContent={<LoyaltyInclusions offer={offer} variant="offer-page" />}
      />
      <LoyaltyPointsWrapper>
        <LoyaltyPointsEarnWithBreakdownModal
          calculationRequests={pointsEarnCalculationRequests}
          calculationType="exact"
        />
      </LoyaltyPointsWrapper>
    </AccommodationIndividualSelectRoomOptionTileDetails>

    <WalledContent
      enforced={offer.walledGarden}
      hidePricing={offer.hidePricing}
      wall={<AccommodationIndividualSelectRoomOptionTilePricing>
        {hasAvailability && <AccommodationIndividualSelectRoomOptionTileCTA>
          {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>{selectedLabel}</TextButton>}
          {!isSelected && <TextButton kind="secondary" onClick={onSelect}>{selectLabel}</TextButton>}
        </AccommodationIndividualSelectRoomOptionTileCTA>}

        {!hasAvailability && <AccommodationIndividualSelectRoomOptionTileMessage>
          <MessageBanner kind="warning" density="compact">
            {intl.formatMessage(
              {
                id: 'T2NCr4cW3DxMvx-TSTXk',
                defaultMessage: 'No {saleUnit} left',
                description: 'Warning message when no rooms/units are available for this option',
              },
              { saleUnit: getSaleUnitPluralNoun(offer.saleUnit) },
            )}
          </MessageBanner>
        </AccommodationIndividualSelectRoomOptionTileMessage>}
      </AccommodationIndividualSelectRoomOptionTilePricing>}
    >
      <AccommodationIndividualSelectRoomOptionTilePricing>
        <BedbankMultiIndividualOptionTilePricing
          promotion={promotion}
          rate={rate}
          bundleRate={rate}
          flightPrice={flightPrice}
          departureTooEarly={departureTooEarly}
          ratePosition={packageNumber}
          showPoints={isDesktopLayout}
          rateListId={rateListId}
          roomPosition={roomPosition}
          roomListId={roomListId}
          discountPosition="above"
        />
      </AccommodationIndividualSelectRoomOptionTilePricing>

      {hasAvailability && <AccommodationIndividualSelectRoomOptionTileCTA>
        {isSelected && <TextButton kind="primary" startIcon={<LineCheckIcon />} nonInteractable>{selectedLabel}</TextButton>}
        {!isSelected && <TextButton kind="secondary" onClick={onSelect}>{selectLabel}</TextButton>}
      </AccommodationIndividualSelectRoomOptionTileCTA>}

      {!hasAvailability && <AccommodationIndividualSelectRoomOptionTileMessage>
        <MessageBanner kind="warning" density="compact">
          {intl.formatMessage(
            {
              id: 'T2NCr4cW3DxMvx-TSTXk',
              defaultMessage: 'No {saleUnit} left',
              description: 'Warning message when no rooms/units are available for this option',
            },
            { saleUnit: getSaleUnitPluralNoun(offer.saleUnit) },
          )}
        </MessageBanner>
      </AccommodationIndividualSelectRoomOptionTileMessage>}
    </WalledContent>
  </AccommodationIndividualSelectRoomOptionTile>
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'offer'
  | 'rooms'
  | 'flightOrigin'
  | 'flightsEnabled'
  | 'isFlightsSelected'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkInDate: state.checkInDate,
  checkOutDate: state.checkOutDate,
  offer: state.bedbankOffer!,
  rooms: state.rooms,
  flightOrigin: state.airport,
  flightsEnabled: offerFlightsEnabled(state, appState),
  isFlightsSelected: state.flights,
}), BedbankIndividualRoomSelectRoomOption)
